import PermissionButton from '../../permissions/PermissionButton';
import { openVentileDevis } from './VentilePrintUtility';
// Update the preview function for both types
// Enhanced previewDevis with PDF generation like printFacture
import { Modal, Spin, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ReactDOM from 'react-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { getDevisById, getItemsInDevis } from '../../models/devisAPI';

// Improved utility function to ensure data integrity between devis and items
const ensureDataLoaded = (devis, items, commercial, callback) => {
  // Create loading modal
  const loadingModalDiv = document.createElement('div');
  document.body.appendChild(loadingModalDiv);
  
  const closeLoadingModal = () => {
    ReactDOM.unmountComponentAtNode(loadingModalDiv);
    if (loadingModalDiv.parentNode) {
      loadingModalDiv.parentNode.removeChild(loadingModalDiv);
    }
  };
  
  // Show loading modal
  ReactDOM.render(
    <Modal
      title="Chargement des données"
      open={true}
      closable={true}
      maskClosable={false}
      footer={[
        <Button key="exit" onClick={closeLoadingModal}>
          Annuler
        </Button>
      ]}
    >
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spin 
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          tip="Vérification et chargement des données..."
        />
      </div>
    </Modal>,
    loadingModalDiv
  );
  
  // Function to fetch fresh data from server to ensure consistency
  const fetchFreshData = async () => {
    try {
      if (!devis) {
        throw new Error('Données du devis manquantes');
      }
      
      // We need the database ID - not REF_DEV
      const devisId = devis.id;
      
      if (!devisId) {
        throw new Error('ID du devis manquant');
      }
      
      console.log('Fetching fresh data for devis ID:', devisId);
      
      // Fetch fresh data from API - both calls need the numeric ID
      let freshDevis, freshItems;
      
      try {
        // First get the devis header
        const devisResponse = await getDevisById(devisId);
        freshDevis = devisResponse.data;
        
        if (!freshDevis) {
          throw new Error('Données du devis incomplètes');
        }
        
        // Then get the items using the SAME numeric ID
        const itemsResponse = await getItemsInDevis(devisId);
        freshItems = itemsResponse.data;
      } catch (apiError) {
        console.error('API Error:', apiError);
        throw new Error(`Erreur API: ${apiError.message}`);
      }
      
      // Verify we have valid data
      if (!freshDevis || !freshItems || !Array.isArray(freshItems)) {
        throw new Error('Données incomplètes ou invalides');
      }
      
      console.log('Fresh data loaded successfully:', { 
        devis: freshDevis.REF_DEV, 
        itemCount: freshItems.length 
      });
      
      // Close loading modal and proceed with callback using fresh data
      closeLoadingModal();
      callback(freshDevis, freshItems, commercial);
      return true;
    } catch (error) {
      console.error('Error fetching fresh data:', error);
      closeLoadingModal();
      
      // Show error modal
      Modal.error({
        title: 'Erreur de chargement',
        content: `Impossible de charger les données les plus récentes: ${error.message || 'Erreur inconnue'}`,
      });
      return false;
    }
  };
  
  // Check if initial data is valid before even trying to fetch
  if (!devis || !devis.id) {
    closeLoadingModal();
    Modal.error({
      title: 'Données manquantes',
      content: 'ID du devis manquant. Impossible de charger les données.',
    });
    return false;
  }
  
  // Start fetching fresh data
  fetchFreshData();
  return false; // Always return false as the callback will be handled async
};

// Templates as template literals
// Finding and modifying the relevant style sections in the internalTemplate

// Update the CSS for ref-text class to make it much larger
const internalTemplate = `<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Devis Interne - {{REF_DEV}}</title>
    <style>
        body {
            font-family: "Courier New", monospace;
            margin: 0;
            padding: 4px;
            background-color: #ffffff;
            font-size: 12px;
            line-height: 1.3;
        }
        .invoice-container { 
            width: 100%; 
            margin: 0 auto; 
        }
        .header-table {
            width: 100%;
            border-collapse: collapse;
        }
        .header-section { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 2px;
            border-bottom: 1px solid #000;
        }
        .mid-section, .bottom-section { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 2px;
        }
        .header-section td, .mid-section td, .bottom-section td { 
            padding: 1px;
            vertical-align: top; 
        }
        .label { 
            font-weight: normal; 
            color: #000;
            font-size: 12px;
        }
        .box { 
            border: 1px solid #000; 
            padding: 2px;
            margin-bottom: 2px;
        }
        .order-title { 
            font-size: 14px;
            font-weight: bold; 
            text-align: center; 
            margin: 2px 0;
            color: #000; 
        }
        table.content-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 2px;
            font-size: 13px;
        }
        table.content-table th, table.content-table td { 
            border: 1px solid #000; 
            padding: 1px;
            text-align: left; 
        }
        table.content-table td {
            font-weight: bold;
        }
        table.content-table th { 
            background-color: #f0f0f0; 
            text-align: center; 
            padding: 1px;
            font-size: 10px;
            font-weight: normal;
        }
        .separator { 
            margin: 1px 0;
            border: 0; 
            border-top: 1px solid #b0b0b0; 
        }
        .text-right { text-align: right; }
        .secondary-info { 
            color: #555; 
            font-size: 10px;
            font-weight: bold;
        }
        /* MODIFIED: Increased font size for REF_DEV value to 2x original size */
        .ref-text {
            font-size: 20px; /* Increased from 14px to 28px (2x) */
            font-weight: bold;
        }
        .page-info {
            font-size: 13px;
            font-weight: bold;
        }
        .validation-info {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 1px;
        }
        .total-amount {
            font-weight: bold;
            font-size: 14px;
        }
        
        /* Pagination control */
        @page {
            size: A4;
            margin: 0.5cm;
        }
        
        /* Headers on every page */
        thead {
            display: table-header-group;
        }
        
        /* We'll handle page breaks with JavaScript instead of CSS */
        
        @media print {
            body { 
                -webkit-print-color-adjust: exact !important;
            }
        }
        
        /* ADDED: Special styling for REF_DEV in order-title */
        .ref-dev-big {
            font-size: 24px; /* Larger font for REF_DEV in the order title */
        }
    </style>
</head>
<body>
    <div class="invoice-container">
        <table class="header-table">
            <thead>
                <tr>
                    <td>
                        <table class="header-section">
                            <tr>
                                <td><span class="label">Cont:</span> <span style="font-weight:bold;">............</span></td>
                                <td><span class="label">H_c:</span> <span style="font-weight:bold;">..../......</span></td>
                                <td><span class="label">Zone/Cont:</span> <span style="font-weight:bold;">......</span></td>
                                <td><span class="label">Val_Fac_par:</span> <span style="font-weight:bold;">/...........</span></td>
                                <td style="text-align: right;"><span class="label">Ref:</span> <span class="ref-text">{{REF_DEV}}</span></td>
                            </tr>
                        </table>
                    </td>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>
                        <!-- Mid Section -->
                        <table class="mid-section">
                            <tr>
                                <td style="width: 30%; vertical-align: top;">
                                    <table class="box" width="100%">
                                        <tr>
                                            <td style="width: 50%;"><span class="label">Nombre_cartons:</span></td>
                                            <td style="width: 50%; font-weight: bold;">.............</td>
                                        </tr>
                                        <tr>
                                            <td style="width: 50%;"><span class="label">BL N°:</span></td>
                                            <td style="width: 50%; font-weight: bold;">....................</td>
                                        </tr>
                                        <tr>
                                            <td style="width: 50%;"><span class="label">Mnt_bl & fact:</span></td>
                                            <td style="width: 50%; font-weight: bold;">....................</td>
                                        </tr>
                                        <tr>
                                            <td style="width: 50%;"><span class="label">Date_Fa:</span></td>
                                            <td style="width: 50%; font-weight: bold;">....................</td>
                                        </tr>
                                        <tr>
                                            <td style="width: 50%;"><span class="label">Fact_N°:</span></td>
                                            <td style="width: 50%; font-weight: bold;">....................</td>
                                        </tr>
                                        <tr>
                                            <td style="width: 50%;"><span class="label">Cat_Clt:</span></td>
                                            <td style="width: 50%; font-weight: bold;">{{CAT_CLT}}</td>
                                        </tr>
                                    </table>
                                </td>

                                <td style="width: 70%; vertical-align: top;">
                                    <table class="box" width="100%">
                                        <tr>
                                            <td style="width: 15%;"><span class="label">Date Devis:</span></td>
                                            <td style="width: 50%; font-weight: bold;">{{DATEVALID}}, {{HEUREVALID}}</td>
                                        </tr>
                                        <tr>
                                            <td><span class="label">Date Imp:</span></td>
                                            <td style="font-weight: bold;">{{CURRENT_DATE}} <span style="float: right;"><span class="label">mnt:</span> <span class="total-amount">{{MNT_TTC}} DH</span></span></td>
                                        </tr>
                                    </table>

                                    <table class="box" width="100%">
                                        <tr>
                                            <td colspan="2"><span class="label">Clt:</span> <span style="font-weight: bold; font-size: 14px;">{{CLIENT}} ({{COMPTE}})</span></td>
                                           
                                        </tr>
                                        <tr>
                                            <td colspan="2"><span class="label">Adrs:</span> <span style="font-weight: bold; font-size: 10px;">{{ADRESSE}}</span></td>
                                        </tr>
                                        <tr>
                                            <td style="width: 50%;"><span class="label">Ville:</span> <span style="font-weight: bold; font-size: 10px;">{{VILLE}}</span></td>
                                            <td style="width: 50%;"><span class="label">Tél:</span> <span style="font-weight: bold; font-size: 10px;">{{TEL}}</span></td>
                                        </tr>
                                    </table>
                                </td>
                            </tr>
                        </table>

                        <!-- Bottom Section -->
                        <table class="bottom-section">
                            <tr>
                                <td>
                                    <table class="box" width="100%">
                                        <tr>
                                            <td style="width: 20%;"><span class="label">M_liv:</span> <span style="font-weight: bold;">{{MODELIV}}</span></td>
                                            <td style="width: 20%;"><span class="label">M_paie:</span> <span style="font-weight: bold;">{{MODE_PAIE}}</span></td>
                                            <td style="width: 60%;"><span class="label">Conditions:</span> <span style="font-weight: bold;">{{conditions}}</span></td>
                                        </tr>
                                        <tr>
                                            <td colspan="3"><span class="label">Remarque:</span> <span style="font-weight: bold;">{{REMARQUE}}</span></td>
                                        </tr>
                                    </table>
                                </td>
                            </tr>
                        </table>

                        <!-- MODIFIED: Updated order-title to use a span with ref-dev-big class for the REF_DEV part -->
                        <div class="order-title">DEVIS : <span class="ref-dev-big">{{REF_DEV}}</span> | Page {{PAGE_NUM}}/{{TOTAL_PAGES}}</div>

                        <div class="table-container">
                            <table class="content-table">
                                <thead>
                                    <tr>
                                        <th style="width: 18%;">Code Art.</th>
                                        <th style="width: 59%;">Article</th>
                                        <th style="width: 8%;">Stock</th>
                                        <th class="text-right" style="width: 5%;">Qte</th>
                                        <th class="text-right" style="width: 5%;">Qte</th>
                                        <th class="text-right" style="width: 5%;">cont</th>
                                    </tr>
                                    <tr>
                                        <th style="width: 18%;">Etage</th>
                                        <th style="width: 59%;">Réf.</th>
                                        <th style="width: 8%;">Cart/pqt</th>
                                        <th class="text-right" style="width: 5%;">cmd</th>
                                        <th class="text-right" style="width: 5%;">liv</th>
                                        <th class="text-right" style="width: 5%;"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {{ITEMS_TABLE}}
                                </tbody>
                            </table>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</body>
</html>`;

// Commercial template - similar to internal but without Etage, Qte liv, cont columns and BLN fields
const commercialTemplate = `<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Devis Commercial - {{REF_DEV}}</title>
    <style>
        body {
            font-family: "Courier New", monospace;
            margin: 0;
            padding: 4px;
            background-color: #ffffff;
            font-size: 12px;
            line-height: 1.3;
        }
        .invoice-container { 
            width: 100%; 
            margin: 0 auto; 
        }
        .header-table {
            width: 100%;
            border-collapse: collapse;
        }
        .header-section { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 2px;
            border-bottom: 1px solid #000;
        }
        .mid-section, .bottom-section { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 2px;
        }
        .header-section td, .mid-section td, .bottom-section td { 
            padding: 1px;
            vertical-align: top; 
        }
        .label { 
            font-weight: normal; 
            color: #000;
            font-size: 12px;
        }
        .box { 
            border: 1px solid #000; 
            padding: 2px;
            margin-bottom: 2px;
        }
        .order-title { 
            font-size: 14px;
            font-weight: bold; 
            text-align: center; 
            margin: 2px 0;
            color: #000; 
        }
        table.content-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 2px;
            font-size: 11px;
        }
        table.content-table th, table.content-table td { 
            border: 1px solid #000; 
            padding: 1px;
            text-align: left; 
        }
        table.content-table td {
            font-weight: bold;
            font-size: 11px;
        }
        table.content-table th { 
            background-color: #f0f0f0; 
            text-align: center; 
            padding: 1px;
            font-size: 9px;
            font-weight: normal;
        }
        .separator { 
            margin: 0.5px 0;
            border: 0; 
            border-top: 1px solid #b0b0b0; 
        }
        .text-right { text-align: right; }
        .secondary-info { 
            color: #555; 
            font-size: 9px;
            font-weight: bold;
        }
        .ref-text {
            font-size: 20px;
            font-weight: bold;
        }
        .page-info {
            font-size: 13px;
            font-weight: bold;
        }
        .total-amount {
            font-weight: bold;
            font-size: 14px;
        }
        
        @page {
            size: A4;
            margin: 0.5cm;
        }
        
        thead {
            display: table-header-group;
        }
        
        @media print {
            body { 
                -webkit-print-color-adjust: exact !important;
            }
        }
        
        .ref-dev-big {
            font-size: 24px;
        }
    </style>
</head>
<body>
    <div class="invoice-container">
        <table class="header-table">
            <thead>
                <tr>
                    <td>
                        <table class="header-section">
                            <tr>
                                <td style="text-align: right;"><span class="label">Ref:</span> <span class="ref-text">{{REF_DEV}}</span></td>
                            </tr>
                        </table>
                    </td>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>
                        <!-- Mid Section -->
                        <table class="mid-section">
                            <tr>
                                <td style="width: 100%; vertical-align: top;">
                                    <table class="box" width="100%">
                                        <tr>
                                            <td style="width: 15%;"><span class="label">Date Devis:</span></td>
                                            <td style="width: 50%; font-weight: bold;">{{DATEVALID}}, {{HEUREVALID}}</td>
                                            <td style="text-align: right;"><span class="label">mnt:</span> <span class="total-amount">{{MNT_TTC}} DH</span></td>
                                        </tr>
                                        <tr>
                                            <td><span class="label">Date Imp:</span></td>
                                            <td style="font-weight: bold;">{{CURRENT_DATE}}</td>
                                        </tr>
                                    </table>

                                    <table class="box" width="100%">
                                        <tr>
                                            <td style="width: 70%;"><span class="label">Clt:</span> <span style="font-weight: bold; font-size: 14px;">{{CLIENT}} ({{COMPTE}})</span></td>
                                            <td style="width: 30%;"><span class="label">Commercial:</span> <span style="font-weight: bold; font-size: 10px;">{{COMMERCIAL}}</span></td>
                                        </tr>
                                        <tr>
                                            <td colspan="2"><span class="label">Adrs:</span> <span style="font-weight: bold; font-size: 10px;">{{ADRESSE}}</span></td>
                                        </tr>
                                        <tr>
                                            <td style="width: 50%;"><span class="label">Ville:</span> <span style="font-weight: bold; font-size: 10px;">{{VILLE}}</span></td>
                                            <td style="width: 50%;"><span class="label">Tél:</span> <span style="font-weight: bold; font-size: 10px;">{{TEL}}</span></td>
                                        </tr>
                                    </table>
                                </td>
                            </tr>
                        </table>

                        <!-- Bottom Section -->
                        <table class="bottom-section">
                            <tr>
                                <td>
                                    <table class="box" width="100%">
                                        <tr>
                                            <td style="width: 20%;"><span class="label">M_liv:</span> <span style="font-weight: bold;">{{MODELIV}}</span></td>
                                            <td style="width: 20%;"><span class="label">M_paie:</span> <span style="font-weight: bold;">{{MODE_PAIE}}</span></td>
                                            <td style="width: 60%;"><span class="label">Conditions:</span> <span style="font-weight: bold;">{{conditions}}</span></td>
                                        </tr>
                                        <tr>
                                            <td colspan="3"><span class="label">Remarque:</span> <span style="font-weight: bold;">{{REMARQUE}}</span></td>
                                        </tr>
                                    </table>
                                </td>
                            </tr>
                        </table>

                        <div class="order-title">DEVIS : <span class="ref-dev-big">{{REF_DEV}}</span> | Page {{PAGE_NUM}}/{{TOTAL_PAGES}}</div>

                        <div class="table-container">
                            <table class="content-table">
                                <thead>
                                    <tr>
                                        <th style="width: 14%;">Code Art.</th>
                                        <th style="width: 40%;">Article</th>
                                        <th style="width: 7%;">Stock</th>
                                        <th class="text-right" style="width: 6%;">Qte</th>
                                        <th class="text-right" style="width: 10%;">PV TTC</th>
                                        <th class="text-right" style="width: 7%;">R%</th>
                                        <th class="text-right" style="width: 6%;">TVA</th>
                                        <th class="text-right" style="width: 15%;">Total TTC</th>
                                    </tr>
                                    <tr>
                                        <th style="width: 14%;">Réf.</th>
                                        <th style="width: 50%;"></th>
                                        <th style="width: 7%;">Cart/pqt</th>
                                        <th class="text-right" style="width: 6%;">cmd</th>
                                        <th class="text-right" style="width: 10%;"></th>
                                        <th class="text-right" style="width: 7%;"></th>
                                        <th class="text-right" style="width: 6%;"></th>
                                        <th class="text-right" style="width: 15%;"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {{ITEMS_TABLE}}
                                </tbody>
                            </table>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</body>
</html>`;

// External template completely redesigned to be more compact
const externalTemplate = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Devis - {{REF_DEV}}</title>
    <style>
        body {
            font-family: "Courier New", monospace;
            margin: 0;
            padding: 8px;
            background-color: #ffffff;
            font-size: 12px;
            line-height: 1.2;
        }
        .invoice-container { 
            width: 100%; 
            margin: 0 auto;
            max-height: 42vh;
            overflow: hidden;
        }
        .header { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 8px;
        }
        .content-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 8px 0;
        }
        .content-table th, .content-table td { 
            border: 1px solid #000; 
            padding: 4px;
            font-size: 11px;
        }
        .content-table td {
            font-weight: bold;
        }
        .content-table th { 
            background-color: #f0f0f0; 
            text-align: center;
            font-size: 10px;
            font-weight: normal;
        }
        .etage-title {
            font-weight: bold;
            font-size: 12px;
            margin: 4px 0;
            border-bottom: 1px solid #000;
            padding-bottom: 2px;
        }
        @media print {
            @page { 
                size: A4; 
                margin: 0.7cm 0.5cm;
            }
            body { -webkit-print-color-adjust: exact !important; }
            .page-break-after {
                page-break-after: always;
                height: 50vh;
            }
        }
        .client-info {
            text-align: right;
            font-weight: bold;
        }
        .client-info .field-label {
            font-weight: normal;
        }
        .page-info {
            font-size: 10px;
            text-align: right;
            margin-top: 2px;
        }
    </style>
</head>
<body>
    <div class="invoice-container">
        <table class="header">
            <tr>
                <td style="width: 50%;">
                    <div><b>DEVIS N° {{REF_DEV}}</b></div>
                    <div><span style="font-weight: normal;">Date:</span> <span style="font-weight: bold;">{{DATE_DEV}} | {{HEUREVALID}}</span></div>
                    
                </td>
                <td class="client-info">
                    <div><b>{{CLIENT}}</b>-<span style="font-weight: bold;">{{VILLE}}</span></div><span style="font-weight: bold;">({{COMPTE}})</span></div>
                   
                </td>
            </tr>
        </table>
        
        <div class="etage-title">ÉTAGE: {{ETAGE_TITLE}}</div>

        <table class="content-table">
            <thead>
                <tr>
                    <th style="width: 61%;">Designation</th>
                    <th style="width: 10%;">Zone</th>
                    <th style="width: 15%;">Code Art.</th>
                    <th style="width: 7%;">Qté</th>
                    <th style="width: 7%;">Qté Prépa</th>
                </tr>
            </thead>
            <tbody>
                {{ITEMS_TABLE}}
            </tbody>
        </table>
    </div>
    <div class="page-break-after"></div>
</body>
</html>
`;

// Utility functions
const formatDate = (date) => date ? new Date(date).toLocaleDateString() : 'Non validé';
const formatNumber = (num) => (Number(num) || 0).toFixed(2);

// Template handling
const processTemplate = (template, replacements) => {
    try {
        let processedTemplate = template;
        Object.entries(replacements).forEach(([key, value]) => {
            const regex = new RegExp(`{{${key}}}`, 'g');
            processedTemplate = processedTemplate.replace(regex, value || '');
        });
        return processedTemplate;
    } catch (error) {
        console.error('Error processing template:', error);
        throw new Error('Failed to process template');
    }
};

// Table creation functions
// Updated createInternalItemsTable function to better handle pagination
const createInternalItemsTable = (items) => {
    if (!items || !items.length) return '';
    
    // Sort items by CODE_ART as characters/strings
    const sortedItems = [...items].sort((a, b) => {
        // Handle null or undefined CODE_ART values
        const codeA = a.CODE_ART || '';
        const codeB = b.CODE_ART || '';
        
        // Simple string comparison
        return codeA.localeCompare(codeB);
    });
    
    return sortedItems.map(item => {
        const stock = item.article?.stocks?.[0];
        return `
        <tr>
            <td>${item.CODE_ART || ''}<hr class="separator"><span class="secondary-info">${stock?.Etage || ''}: ${stock?.Zone || ''}</span></td>
            <td>${item.ARTICLE || ''}<hr class="separator"><span class="secondary-info">${item.article?.REF_OEM || ''}</span></td>
            <td>${stock?.qte_stk || '0'}<hr class="separator"><span class="secondary-info">${item.article?.qt_cart || '0'}/${item.article?.q_paquet || '0'}</span></td>
            <td class="text-right">${item.QTE || ''}</td>
            <td class="text-right"></td>
            <td class="text-right"></td>
        </tr>
    `;
    }).join('');
};

// Updated createExternalItemsTable to match new requirements
const createExternalItemsTable = (items) => {
    return items.map(item => {
        const stock = item.article?.stocks?.[0];
        return `
            <tr>
                <td>${item.ARTICLE || ''}</td>
                <td>${stock?.Zone || ''}</td>
                <td>${item.CODE_ART || ''}</td>
                <td style="text-align: center;">${item.QTE || ''}</td>
                <td></td>
            </tr>
        `;
    }).join('');
};

// Create commercial items table - without Etage, Qte liv, cont columns
const createCommercialItemsTable = (items) => {
    if (!items || !items.length) return '';
    
    // Sort items by CODE_ART as characters/strings
    const sortedItems = [...items].sort((a, b) => {
        const codeA = a.CODE_ART || '';
        const codeB = b.CODE_ART || '';
        return codeA.localeCompare(codeB);
    });
    
    return sortedItems.map(item => {
        const stock = item.article?.stocks?.[0];
        const pvTTC = Number(item.PV_TTC || 0).toFixed(2);
        const remise = Number(item.REMISE || 0).toFixed(2);
        const tva = Number(item.TVA || 0).toFixed(0);
        
        // Calculate total TTC with remise applied
        const qte = Number(item.QTE || 0);
        const price = Number(item.PV_TTC || 0);
        const remisePercent = Number(item.REMISE || 0);
        const discountedPrice = price * (1 - remisePercent / 100);
        const totalTTC = (qte * discountedPrice).toFixed(2);
        
        return `
        <tr>
            <td>${item.CODE_ART || ''}<hr class="separator"><span class="secondary-info">${item.article?.REF_OEM || ''}</span></td>
            <td>${item.ARTICLE || ''}</td>
            <td>${stock?.qte_stk || '0'}<hr class="separator"><span class="secondary-info">${item.article?.qt_cart || '0'}/${item.article?.q_paquet || '0'}</span></td>
            <td class="text-right">${item.QTE || ''}</td>
            <td class="text-right">${pvTTC}</td>
            <td class="text-right">${remise}%</td>
            <td class="text-right">${tva}%</td>
            <td class="text-right">${totalTTC}</td>
        </tr>
    `;
    }).join('');
};

const getRemiseGlobalRow = (remiseG) => {
    return remiseG ? `
        <tr>
            <th>Remise Globale</th>
            <td>${formatNumber(remiseG)}%</td>
        </tr>
    ` : '';
};

// Print handling
const createPrintFrame = (html) => {
    const iframe = document.createElement('iframe');
    iframe.style.visibility = 'hidden';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    
    return iframe;
};

// Main print function
// Advanced pagination for invoice printing - modified for mixed item counts
export const enhancePrintingWithPagination = (devis, items, iframe, type = 'internal') => {
    try {
        // Wait for iframe to be ready
        setTimeout(() => {
            const doc = iframe.contentWindow.document;
            
            // Get the reference number
            const refNumber = devis.REF_DEV;
            
            // Choose the correct table creation function based on type
            const createItemsTable = type === 'commercial' ? createCommercialItemsTable : createInternalItemsTable;
            
            // Define our item counts per page
            const FIRST_PAGE_ITEMS = 19;
            const OTHER_PAGES_ITEMS = 24;
            
            // Calculate total pages with mixed item counts
            let totalPages = 1;
            let remainingItems = items.length - FIRST_PAGE_ITEMS;
            
            if (remainingItems > 0) {
                totalPages += Math.ceil(remainingItems / OTHER_PAGES_ITEMS);
            }
            
            // First, update all total pages references
            doc.querySelectorAll('.page-info').forEach(element => {
                element.innerHTML = element.innerHTML.replace(/\/\d+/, '/' + totalPages);
            });
            
            // Update the order title on the first page
            const firstPageTitle = doc.querySelector('.order-title');
            if (firstPageTitle) {
                firstPageTitle.innerHTML = `DEVIS : <span class="ref-dev-big" style="font-size: 24px; font-weight: bold;">${refNumber}</span> | Page 1/${totalPages}`;
            }
            
            // Get the table and its parts
            const table = doc.querySelector('.content-table');
            const thead = table.querySelector('thead');
            const tbody = table.querySelector('tbody');
            
            // Store the original thead HTML for reuse on other pages
            const theadHTML = thead.innerHTML;
            
            // Always populate the first page with items
            const firstPageItems = items.slice(0, FIRST_PAGE_ITEMS);
            tbody.innerHTML = createItemsTable(firstPageItems);
            
            // If we have multiple pages, we need to modify the table structure
            if (totalPages > 1) {
                // Create a container for all pages
                const pagesContainer = doc.createElement('div');
                
                // Create subsequent pages with their own tables and headers
                for (let page = 2; page <= totalPages; page++) {
                    // Calculate the start index for this page (after the first page)
                    const startIdx = FIRST_PAGE_ITEMS + ((page - 2) * OTHER_PAGES_ITEMS);
                    // Get items for this page - 22 items per page after first page
                    const pageItems = items.slice(startIdx, Math.min(startIdx + OTHER_PAGES_ITEMS, items.length));
                    
                    // Create a page container with a page break
                    const pageContainer = doc.createElement('div');
                    pageContainer.className = 'page-container';
                    pageContainer.style.pageBreakBefore = 'always';
                    
                    // Add the title for this page
                    const pageTitle = doc.createElement('div');
pageTitle.className = 'order-title';
pageTitle.innerHTML = `DEVIS : <span class="ref-dev-big" style="font-size: 24px; font-weight: bold;">${refNumber}</span> | Page ${page}/${totalPages}`;
pageContainer.appendChild(pageTitle);
                    
                    // Create a new table for this page
                    const pageTable = doc.createElement('table');
                    pageTable.className = 'content-table';
                    pageTable.style.width = '100%';
                    pageTable.style.borderCollapse = 'collapse';
                    pageTable.style.marginTop = '4px';
                    pageTable.style.fontSize = type === 'commercial' ? '11px' : '13px';
                    
                    // Add the thead with column headers
                    const pageHead = doc.createElement('thead');
                    pageHead.innerHTML = theadHTML;
                    pageTable.appendChild(pageHead);
                    
                    // Add the tbody with items
                    const pageBody = doc.createElement('tbody');
                    pageBody.innerHTML = createItemsTable(pageItems);
                    pageTable.appendChild(pageBody);
                    
                    // Add the table to the page container
                    pageContainer.appendChild(pageTable);
                    
                    // Add the page container to the document
                    pagesContainer.appendChild(pageContainer);
                }
                
                // Add all pages after the first page's table
                table.parentNode.appendChild(pagesContainer);
                
                // Add CSS for controlling page breaks and formatting
                const style = doc.createElement('style');
                style.textContent = `
                    @media print {
                        /* Keep table headers together with their content */
                        table { page-break-inside: auto; }
                        tr { page-break-inside: avoid; }
                        thead { display: table-header-group; }
                        
                        /* Hide all headers and other elements on pages after the first */
                        .page-container .header-section,
                        .page-container .mid-section,
                        .page-container .bottom-section {
                            display: none;
                        }
                        
                        /* Control spacing for page title */
                        .page-container .order-title {
                            margin-top: 12px;
                            margin-bottom: 6px;
                        }
                    }
                `;
                doc.head.appendChild(style);
            }
            
            // Trigger print after all changes applied
            setTimeout(() => {
                iframe.contentWindow.print();
                
                // Remove iframe after printing
                setTimeout(() => {
                    if (iframe && iframe.parentNode) {
                        iframe.parentNode.removeChild(iframe);
                    }
                }, 1000);
            }, 500);
        }, 300);
    } catch (error) {
        console.error('Error setting up pagination:', error);
    }
};

// Function to group items by etage
const groupItemsByEtage = (items) => {
    const groups = new Map();
    
    // Group items by their etage
    items.forEach(item => {
        const stock = item.article?.stocks?.[0];
        const etage = stock?.Etage !== undefined && stock?.Etage !== null 
            ? stock.Etage.toString() 
            : 'Non défini';
        
        if (!groups.has(etage)) {
            groups.set(etage, []);
        }
        
        groups.get(etage).push(item);
    });
    
    // Convert to array of { etage, items } objects, sorted by etage
    return Array.from(groups.entries())
        .map(([etage, items]) => ({ etage, items }))
        .sort((a, b) => {
            // If both are numbers, use numeric comparison
            if (!isNaN(a.etage) && !isNaN(b.etage)) {
                return parseInt(a.etage) - parseInt(b.etage);
            }
            // "Non défini" should be at the end
            if (a.etage === 'Non défini') return 1;
            if (b.etage === 'Non défini') return -1;
            // Otherwise, use string comparison
            return a.etage.localeCompare(b.etage);
        });
};

// Updated printDevis function to handle etage-based printing for external and commercial
export const printDevis = async (devis, items, commercial, type = 'internal') => {
    if (type === 'external') return openVentileDevis([devis]);
    try {
        // Create a wrapper function that will use the fresh data without calling recursively
        const performPrint = (validDevis, validItems, validCommercial) => {
            try {
                if (type === 'internal') {
                    // Use the original internal printing logic
                    const template = internalTemplate;
                    
                    // Create a copy of the items array to avoid modifying the original
                    const sortedItems = [...validItems].sort((a, b) => {
                        // Handle null or undefined CODE_ART values
                        const codeA = a.CODE_ART || '';
                        const codeB = b.CODE_ART || '';
                        
                        // Simple string comparison
                        return codeA.localeCompare(codeB);
                    });
                    
                    const replacements = getTemplateReplacements(validDevis, sortedItems, validCommercial, type);
                    const html = processTemplate(template, replacements);
                    const iframe = createPrintFrame(html);
                    
                    iframe.onload = () => {
                        enhancePrintingWithPagination(validDevis, sortedItems, iframe, 'internal');
                    };
                } else if (type === 'commercial') {
                    // Use the commercial template
                    const template = commercialTemplate;
                    
                    // Create a copy of the items array to avoid modifying the original
                    const sortedItems = [...validItems].sort((a, b) => {
                        const codeA = a.CODE_ART || '';
                        const codeB = b.CODE_ART || '';
                        return codeA.localeCompare(codeB);
                    });
                    
                    const replacements = getTemplateReplacements(validDevis, sortedItems, validCommercial, type);
                    const html = processTemplate(template, replacements);
                    const iframe = createPrintFrame(html);
                    
                    iframe.onload = () => {
                        enhancePrintingWithPagination(validDevis, sortedItems, iframe, 'commercial');
                    };
                }
            } catch (printError) {
                console.error('Error in performPrint:', printError);
                Modal.error({
                    title: 'Erreur d\'impression',
                    content: `Une erreur s'est produite lors de l'impression: ${printError.message}`,
                });
            }
        };

        // Use ensureDataLoaded with our non-recursive wrapper
        ensureDataLoaded(devis, items, commercial, performPrint);
    } catch (error) {
        console.error('Error printing devis:', error);
        Modal.error({
            title: 'Erreur d\'impression',
            content: `Échec de l'impression du devis: ${error.message}`,
        });
    }
};

// Updated template replacement function to handle page-specific data with mixed page sizes
const getTemplateReplacements = (devis, items, commercial, type) => {
    // Special calculation for mixed item counts (17 for first page, 20 for rest)
    let totalPages = 1;
    
    if (type === 'internal' || type === 'commercial') {
        const FIRST_PAGE_ITEMS = 17;
        const OTHER_PAGES_ITEMS = 20;
        const remainingItems = items.length - FIRST_PAGE_ITEMS;
        
        if (remainingItems > 0) {
            totalPages += Math.ceil(remainingItems / OTHER_PAGES_ITEMS);
        }
    }
    
    // Format time from the DATE_DEV (assuming it's a Date or can be converted to one)
    const getTimeFromDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = new Date(dateStr);
            return date.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        } catch (e) {
            return 'N/A';
        }
    };
    
    const baseReplacements = {
        REF_DEV: devis.REF_DEV,
        DATE_DEV: formatDate(devis.DATE_DEV),
        HEUREVALID: devis.HEUREVALID,
        DATEVALID: formatDate(devis.DATEVALID),
        CLIENT: devis.CLIENT || 'N/A',
        COMPTE: devis.COMPTE || 'N/A',
        CAT_CLT: devis.client?.nat_clt || 'N/A',
        VILLE: devis.client?.VILLE || 'N/A',
        TEL: devis.client?.TEL || 'N/A',
        COMMERCIAL: commercial?.nom || 'N/A',
        CODE_COM: devis.CODE_COM || 'N/A',
        MODELIV: devis.MODELIV || 'N/A',
        MODE_PAIE: devis.MODE_PAIE || 'N/A',
        ADRESSE: devis.client?.ADRESSE_SIEGE || 'N/A',
        conditions: devis.client?.conditions || 'N/A',
        REMARQUE: devis.REMARQUE || '',
        MNT_HT: formatNumber(devis.MNT_HT),
        MNT_TTC: formatNumber(devis.MNT_TTC),
        CURRENT_DATE: new Date().toLocaleString(undefined, { 
            year: 'numeric', 
            month: 'numeric', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            second: '2-digit',
            hour12: false // This ensures 24-hour format (military time)
        }),
        TOTAL_PAGES: totalPages.toString(),
        PAGE_NUM: '1', // Default value, will be replaced for each page
        ITEMS_TABLE: type === 'internal' ? 
            createInternalItemsTable(items) : 
            (type === 'commercial' ? 
                createCommercialItemsTable(items) : 
                createExternalItemsTable(items))
    };

    if (type === 'external') {
        return {
            ...baseReplacements,
            REMISEG_ROW: getRemiseGlobalRow(devis.REMISEG),
            TVA_TOTAL: formatNumber(devis.MNT_TTC - devis.MNT_HT)
        };
    }

    return baseReplacements;
};

// Initialiser les templates
export const initializePrintTemplates = async () => {
    try {
        // Pas besoin de récupérer des templates externes s'ils sont définis en ligne
        console.log('Print templates loaded successfully');
        return true;
    } catch (error) {
        console.error('Failed to load print templates:', error);
        throw new Error('Failed to initialize print templates');
    }
};

// Generate PDF blob from HTML content


// PDF generation helper function
const generatePdfBlob = async (htmlString, isLandscape = false) => {
    const container = document.createElement('div');
    
    // Detect if this is sideways content (rotated emplacement template)
    const isSidewaysContent = htmlString.includes('transform: rotate(-90deg)');
    const pageSize = 'a4'; // Always use A4 now
    
    // Set container dimensions based on orientation
    container.style.width = isLandscape ? '297mm' : '210mm';
    
    container.style.position = 'absolute';
    container.style.left = '-10000px';
    container.innerHTML = htmlString;
    document.body.appendChild(container);
    
    try {
        const pages = container.querySelectorAll('.page, .document, .invoice-container');
        // Create PDF with the right orientation and page size
        const pdf = new jsPDF(isLandscape ? 'l' : 'p', 'mm', pageSize);
        const margin = 10; // 10mm for sides/bottom
    
        for (let i = 0; i < pages.length; i++) {
            const page = pages[i];
            
            // Set specific styles for capturing based on orientation
            page.style.width = isLandscape ? '297mm' : '210mm';
            page.style.height = isLandscape ? '210mm' : '297mm';
            page.style.padding = `5mm ${margin}mm ${margin}mm ${margin}mm`; // Reduced top padding
            page.style.boxSizing = 'border-box';
            page.style.backgroundColor = '#FFFFFF';
    
            const canvas = await html2canvas(page, {
                scale: 3,
                useCORS: true,
                logging: false,
                letterRendering: true,
                backgroundColor: '#FFFFFF',
                width: page.offsetWidth,
                height: page.offsetHeight,
                fontFamily: 'Arial',
            });
            
            // Add image to PDF - adjust dimensions based on orientation and page size
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            if (i > 0) pdf.addPage();
            
            if (isLandscape) {
                pdf.addImage(imgData, 'JPEG', 0, 0, 297, 210); // A4 landscape dimensions
            } else {
                pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297); // A4 portrait dimensions
            }
        }
    
        return pdf.output('blob');
    } finally {
        document.body.removeChild(container);
    }
};

export const previewDevis = async (devis, items, commercial, type = 'internal') => {
    if (type === 'external') return openVentileDevis([devis], true);
    try {
        // Create a wrapper function that will use the fresh data without calling recursively
        const performPreview = async (validDevis, validItems, validCommercial) => {
            try {
                // Create loading modal with "Sortir" button
                const loadingModalDiv = document.createElement('div');
                document.body.appendChild(loadingModalDiv);
                
                const closeLoadingModal = () => {
                    ReactDOM.unmountComponentAtNode(loadingModalDiv);
                    document.body.removeChild(loadingModalDiv);
                };
                
                ReactDOM.render(
                    <Modal
                        title="Préparation du document"
                        open={true}
                        closable={true}
                        maskClosable={false}
                        footer={[
                            <Button key="exit" onClick={closeLoadingModal}>
                                Sortir
                            </Button>
                        ]}
                    >
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                            <Spin 
                                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                                tip="Préparation en cours..."
                            />
                        </div>
                    </Modal>,
                    loadingModalDiv
                );

                // Generate HTML content based on type
                let html;
                if (type === 'internal') {
                    // Sort items by CODE_ART as characters/strings
                    const sortedItems = [...validItems].sort((a, b) => {
                        // Handle null or undefined CODE_ART values
                        const codeA = a.CODE_ART || '';
                        const codeB = b.CODE_ART || '';
                        
                        // Simple string comparison
                        return codeA.localeCompare(codeB);
                    });
                    
                    // Use the internal template
                    const template = internalTemplate;
                    const replacements = getTemplateReplacements(validDevis, sortedItems, validCommercial, 'internal');
                    html = processTemplate(template, replacements);
                } else if (type === 'commercial') {
                    // Sort items by CODE_ART as characters/strings
                    const sortedItems = [...validItems].sort((a, b) => {
                        const codeA = a.CODE_ART || '';
                        const codeB = b.CODE_ART || '';
                        return codeA.localeCompare(codeB);
                    });
                    
                    // Use the commercial template
                    const template = commercialTemplate;
                    const replacements = getTemplateReplacements(validDevis, sortedItems, validCommercial, 'commercial');
                    html = processTemplate(template, replacements);
                }

                // Add page class if using internal or commercial template to ensure proper PDF generation
                if (type === 'internal' || type === 'commercial') {
                    // Wrap the template content in page divs for proper PDF rendering
                    html = html.replace('<div class="invoice-container">', '<div class="page invoice-container">');
                }

                // Generate the PDF blob
                const pdfBlob = await generatePdfBlob(html);
                
                // Close loading modal
                closeLoadingModal();

                // Create URL for the blob
                const pdfUrl = URL.createObjectURL(pdfBlob);
                
                // Create preview modal with "Sortir" button
                const previewModalDiv = document.createElement('div');
                document.body.appendChild(previewModalDiv);
                
                const closePreviewModal = () => {
                    URL.revokeObjectURL(pdfUrl);
                    ReactDOM.unmountComponentAtNode(previewModalDiv);
                    document.body.removeChild(previewModalDiv);
                };
                
                ReactDOM.render(
                    <Modal
                        title={`Aperçu du Devis - ${validDevis.REF_DEV}`}
                        open={true}
                        width="90%"
                        centered={true}
                        closable={true}
                        maskClosable={true}
                        className="pdf-preview-modal"
                        style={{ top: 20, maxWidth: '1400px' }}
                        footer={[
                            <PermissionButton permissionModule="quotes" permissionAction="print" key="print" type="primary" onClick={() => {
                                const printWindow = window.open(pdfUrl);
                                if (printWindow) {
                                    printWindow.addEventListener('load', () => {
                                        printWindow.print();
                                    });
                                } else {
                                    alert("Veuillez autoriser les fenêtres pop-up pour imprimer");
                                }
                            }}>
                                Imprimer
                            </PermissionButton>,
                            <Button key="exit" onClick={closePreviewModal}>
                                Sortir
                            </Button>
                        ]}
                        onCancel={closePreviewModal}
                    >
                        <div className="pdf-preview-container">
                            <iframe
                                src={pdfUrl}
                                style={{ width: '100%', height: '80vh', border: 'none' }}
                                title="Aperçu du Devis"
                            />
                        </div>
                    </Modal>,
                    previewModalDiv
                );

                return {
                    cleanup: closePreviewModal,
                    pdfUrl
                };
            } catch (previewError) {
                console.error('Error in performPreview:', previewError);
                Modal.error({
                    title: 'Erreur d\'aperçu',
                    content: `Une erreur s'est produite lors de l'aperçu: ${previewError.message}`,
                });
            }
        };

        // Use ensureDataLoaded with our non-recursive wrapper
        ensureDataLoaded(devis, items, commercial, performPreview);
    } catch (error) {
        console.error('Error previewing devis:', error);
        Modal.error({
            title: 'Erreur d\'aperçu',
            content: `Échec de l'aperçu du devis: ${error.message}`,
        });
    }
};




// Emplacement template optimized for A5 landscape page
const emplacementTemplate = `<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Emplacement - {{REF_DEV}}</title>
    <style>
        @page {
            size: A5 landscape;
            margin: 1cm;
        }
        body {
            font-family: "Courier New", monospace;
            margin: 0;
            padding: 0;
            font-size: 14px;
            line-height: 1.4;
            background-color: #ffffff;
            box-sizing: border-box;
        }
        .page-container {
            width: 100%;
            height: 100%;
            box-sizing: border-box;
            padding: 0;
        }
        .document {
            position: relative;
            width: 190mm; /* A5 landscape width minus 2cm margins (210mm - 20mm) */
            height: 128mm; /* A5 landscape height minus 2cm margins (148mm - 20mm) */
            overflow: hidden;
            display: flex;
            flex-direction: column;
            background-color: #ffffff;
            padding: 10px;
            box-sizing: border-box;
            page-break-inside: avoid;
            page-break-after: avoid;
        }
        .top-section {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 8px;
            border-bottom: 3px solid #000;
            padding-bottom: 8px;
            flex-shrink: 0;
        }
        .ref-and-etages {
            flex: 1.4;
            display: flex;
            flex-direction: column;
            gap: 10px;
        }
        .ref-section {
            display: flex;
            align-items: center;
            gap: 15px;
            justify-content: space-between;
        }
        .ref-content {
            display: flex;
            align-items: center;
            gap: 15px;
        }
        .ref_label {
            font-weight: normal;
            font-size: 10px;
            color: #333;
        }
        .ref_value {
            font-size: 36px;
            font-weight: bold;
            letter-spacing: 2px;
        }
        .etages-container {
            display: flex;
            flex-wrap: nowrap;
            gap: 4px;
            width: 100%;
            overflow-x: auto;
        }
        .etage-box {
            border: 1px solid #999;
            background-color: #f8f8f8;
            padding: 3px 6px;
            font-size: 9px;
            text-align: center;
            color: #666;
            min-width: 45px;
            flex-shrink: 0;
        }
        .etage-title {
            font-weight: normal;
            font-size: 7px;
            margin-bottom: 2px;
        }
        .etage-count {
            font-weight: bold;
            font-size: 12px;
            color: #333;
        }
        .total-etage-box {
            border: 2px solid #000;
            background-color: #e8e8e8;
            padding: 4px 8px;
            font-size: 9px;
            text-align: center;
            color: #000;
            min-width: 65px;
            font-weight: bold;
            flex-shrink: 0;
        }
        .total-etage-title {
            font-weight: bold;
            font-size: 11px;
            margin-bottom: 2px;
            color: #000;
        }
        .total-etage-count {
            font-weight: bold;
            font-size: 12px;
            color: #000;
        }
        .info-section {
            flex: 1;
            text-align: right;
            font-size: 15px;
        }
        .account-section {
            margin-bottom: 8px;
            font-size: 15px;
            flex-shrink: 0;
        }
        .field_label {
            font-weight: normal;
            display: inline-block;
            min-width: 70px;
            color: #333;
            font-size: 15px;
        }
        .field_value {
            font-weight: bold;
            font-size: 15px;
            margin-left: 10px;
        }
        .cat_value {
            font-weight: bold;
            font-size: 15px;
            margin-left: 15px;
        }
        .client-info {
            margin-bottom: 8px;
            flex-shrink: 0;
        }
        .client-row {
            margin-bottom: 4px;
            font-size: 15px;
        }
        .client-label {
            font-weight: normal;
            display: inline-block;
            min-width: 70px;
            color: #333;
        }
        .client-value {
            font-weight: bold;
            font-size: 15px;
            margin-left: 10px;
        }
        .delivery-info {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px 15px;
            margin-bottom: 8px;
            font-size: 15px;
            flex-shrink: 0;
        }
        .delivery-field {
        }
        .delivery-label {
            display: inline-block;
            min-width: 70px;
            color: #333;
        }
        .delivery-value {
            font-weight: bold;
        }
        .remarks-section {
            margin: 4px 0;
            font-size: 15px;
            flex-shrink: 0;
            display: flex;
            align-items: flex-start;
        }
        .remarks-label {
            display: inline-block;
            min-width: 70px;
            color: #333;
        }
        .remarks-value {
            font-weight: bold;
        }
        .conditions-section {
            margin: 4px 0;
            font-size: 15px;
            flex-shrink: 0;
            display: flex;
            align-items: flex-start;
        }
        .conditions-label {
            display: inline-block;
            min-width: 70px;
            color: #333;
        }
        .conditions-value {
            font-weight: bold;
        }
        .right-dots {
            font-weight: bold;
            color: #666;
        }
        @media print {
            html, body {
                margin: 0;
                padding: 0;
                height: 100%;
                overflow: hidden;
            }
            .page-container {
                width: 190mm;
                height: 128mm;
                margin: 0;
                padding: 0;
            }
            .document {
                page-break-inside: avoid !important;
                page-break-after: avoid !important;
                page-break-before: avoid !important;
                width: 190mm !important;
                height: 128mm !important;
                max-height: 128mm !important;
                overflow: hidden !important;
            }
        }
    </style>
</head>
<body>
    <div class="page-container">
        <div class="document">
        <div class="top-section">
            <div class="ref-and-etages">
                <div class="ref-section">
                    <div class="ref-content">
                        <span class="ref_label">Réf Devis:</span>
                        <span class="ref_value">{{REF_DEV}}</span>
                    </div>
                    {{TOTAL_BOX}}
                </div>
                <div class="etages-container">
                    {{ETAGES_BOXES}}
                </div>
            </div>
            
            <div class="info-section">
                <div style="margin-bottom: 4px;">Date Devis: <strong>{{DATEVALID}} - {{HEUREVALID}}</strong></div>
                <div style="margin-bottom: 2px;">Fact N°: <span class="right-dots">...............</span></div>
                <div style="margin-bottom: 2px;">Mnt bl & fact: <span class="right-dots">...............</span></div>
                <div style="margin-bottom: 2px;">Date Fact: <span class="right-dots">...............</span></div>
                <div>Qté Cart: <span class="right-dots">...............</span></div>
            </div>
        </div>

        <div class="account-section">
            <span class="field_label">Compte:</span>
            <span class="field_value">{{COMPTE}}</span>
            <span class="cat_value" style="margin-left: 30px;">Ville: {{VILLE}}</span>
            <span class="cat_value" style="margin-left: 30px;">Cat_clt: {{CAT_CLT}}</span>
        </div>

        <div class="client-info">
            <div class="client-row">
                <span class="client-label">Client:</span>
                <span class="client-value">{{CLIENT}}</span>
            </div>
            
            <div class="client-row">
                <span class="client-label">Adresse:</span>
                <span class="client-value">{{ADRESSE}}</span>
            </div>
        </div>
        
        <div class="delivery-info">
            <div class="delivery-field">
                <span class="delivery-label">Mod_liv:</span>
                <span class="delivery-value">{{MODELIV}}</span>
            </div>
            <div class="delivery-field">
                <span class="delivery-label">Mode Paie:</span>
                <span class="delivery-value">{{MODE_PAIE}}</span>
            </div>
        </div>
        
        <div class="remarks-section">
            <span class="remarks-label">Remarque:</span>
            <span class="remarks-value">{{REMARQUE}}</span>
        </div>
        
        <div class="conditions-section">
            <span class="conditions-label">Conditions:</span>
            <span class="conditions-value">{{CONDITIONS}}</span>
        </div>
        </div>
    </div>
</body>
</html>`;

// Function to create etage boxes for emplacement
const createEmplacementEtageBoxes = (items) => {
    // Define the standard etages we want to show
    const standardEtages = ['-1', '0', '1', '2', '3', '4'];
    
    // Group items by etage
    const groupedItems = groupItemsByEtage(items);
    
    // Create a map for easy lookup
    const etageMap = new Map();
    groupedItems.forEach(group => {
        etageMap.set(group.etage, group.items.length);
    });
    
    // Calculate total items
    const totalItems = items ? items.length : 0;
    
    // Create boxes for each standard etage
    let etageBoxes = standardEtages.map(etage => {
        const count = etageMap.get(etage) || 0;
        return `
            <div class="etage-box">
                <div class="total-etage-title">ÉTAGE ${etage}</div>
                <div class="etage-count">${count}</div>
            </div>
        `;
    }).join('');
    
    // Add boxes for any non-standard etages that have items
    groupedItems.forEach(group => {
        if (!standardEtages.includes(group.etage) && group.etage !== 'Non défini') {
            etageBoxes += `
                <div class="etage-box">
                    <div class="total-etage-title">ÉTAGE ${group.etage}</div>
                    <div class="etage-count">${group.items.length}</div>
                </div>
            `;
        }
    });
    
    // Add box for undefined etage if it has items
    if (etageMap.has('Non défini')) {
        etageBoxes += `
            <div class="etage-box">
                <div class="etage-title">NON DÉFINI</div>
                <div class="etage-count">${etageMap.get('Non défini')}</div>
            </div>
        `;
    }
    
    return etageBoxes;
};

// Function to create total box separately
const createTotalBox = (items) => {
    const totalItems = items ? items.length : 0;
    return `
        <div class="total-etage-box">
            <div class="total-etage-title">TOTAL</div>
            <div class="total-etage-count">${totalItems}</div>
        </div>
    `;
};

// Updated getEmplacementReplacements function to include items
const getEmplacementReplacements = (devis, items, commercial) => {
    const etageBoxes = items ? createEmplacementEtageBoxes(items) : '';
    const totalBox = items ? createTotalBox(items) : '';
    
    return {
        REF_DEV: devis.REF_DEV,
        DATEVALID: formatDate(devis.DATEVALID),
        HEUREVALID: devis.HEUREVALID || 'N/A',
        COMPTE: devis.COMPTE || 'N/A',
        CLIENT: devis.CLIENT || 'N/A',
        CAT_CLT: devis.client?.nat_clt || 'N/A',
        VILLE: devis.client?.VILLE || 'N/A',
        ADRESSE: devis.client?.ADRESSE_SIEGE || 'N/A',
        MODELIV: devis.MODELIV || 'N/A',
        MODE_PAIE: devis.MODE_PAIE || 'N/A',
        REMARQUE: devis.REMARQUE || '',
        CONDITIONS: devis.client?.conditions || 'Aucune',
        ETAGES_BOXES: etageBoxes,
        TOTAL_BOX: totalBox
    };
};


export const printDevisEmplacement = async (devis, items, commercial) => {
    try {
        // Create a wrapper function that will use the fresh data
        const performPrint = (validDevis, validItems, validCommercial) => {
            try {
                const template = emplacementTemplate;
                const replacements = getEmplacementReplacements(validDevis, validItems, validCommercial);
                const html = processTemplate(template, replacements);
                
                // Create iframe with custom orientation settings for A4 portrait sideways
                const iframe = document.createElement('iframe');
                iframe.style.visibility = 'hidden';
                iframe.style.position = 'fixed';
                iframe.style.right = '0';
                iframe.style.bottom = '0';
                document.body.appendChild(iframe);
                
                const doc = iframe.contentWindow.document;
                doc.open();
                doc.write(html);
                
                // Add specific CSS for A5 landscape printing
                const style = doc.createElement('style');
                style.textContent = `
                    @page {
                        size: A5 landscape;
                        margin: 1cm;
                    }
                    @media print {
                        html, body {
                            margin: 0;
                            padding: 0;
                            height: 100%;
                            overflow: hidden;
                        }
                        .page-container {
                            width: 190mm;
                            height: 128mm;
                            margin: 0;
                            padding: 0;
                        }
                        .document {
                            page-break-inside: avoid !important;
                            page-break-after: avoid !important;
                            page-break-before: avoid !important;
                            width: 190mm !important;
                            height: 128mm !important;
                            max-height: 128mm !important;
                            overflow: hidden !important;
                        }
                    }
                `;
                doc.head.appendChild(style);
                
                doc.close();
                
                iframe.onload = () => {
                    setTimeout(() => {
                        iframe.contentWindow.print();
                        
                        // Remove iframe after printing
                        setTimeout(() => {
                            if (iframe && iframe.parentNode) {
                                iframe.parentNode.removeChild(iframe);
                            }
                        }, 1000);
                    }, 500);
                };
            } catch (printError) {
                console.error('Error in emplacement print:', printError);
                Modal.error({
                    title: 'Erreur d\'impression',
                    content: `Une erreur s'est produite lors de l'impression: ${printError.message}`,
                });
            }
        };
        
        // Use ensureDataLoaded with our wrapper
        ensureDataLoaded(devis, items, commercial, performPrint);
    } catch (error) {
        console.error('Error printing devis emplacement:', error);
        Modal.error({
            title: 'Erreur d\'impression',
            content: `Échec de l'impression du devis emplacement: ${error.message}`,
        });
    }
};

// Update previewDevisEmplacement function with etage functionality
export const previewDevisEmplacement = async (devis, items, commercial) => {
    try {
        // Create a wrapper function that will use the fresh data
        const performPreview = async (validDevis, validItems, validCommercial) => {
            try {
                // Create loading modal with "Sortir" button
                const loadingModalDiv = document.createElement('div');
                document.body.appendChild(loadingModalDiv);
                
                const closeLoadingModal = () => {
                    ReactDOM.unmountComponentAtNode(loadingModalDiv);
                    document.body.removeChild(loadingModalDiv);
                };
                
                ReactDOM.render(
                    <Modal
                        title="Préparation du document"
                        open={true}
                        closable={true}
                        maskClosable={false}
                        footer={[
                            <Button key="exit" onClick={closeLoadingModal}>
                                Sortir
                            </Button>
                        ]}
                    >
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                            <Spin 
                                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                                tip="Préparation en cours..."
                            />
                        </div>
                    </Modal>,
                    loadingModalDiv
                );

                // Generate HTML content with items data
                const template = emplacementTemplate;
                const replacements = getEmplacementReplacements(validDevis, validItems, validCommercial);
                const html = processTemplate(template, replacements);

                // Add class for the PDF generator to find
                const enhancedHtml = html.replace('<div class="document">', '<div class="document page">');

                // Generate the PDF blob - using portrait for A4 sideways page
                const pdfBlob = await generatePdfBlob(enhancedHtml, false);
                
                // Close loading modal
                closeLoadingModal();

                // Create URL for the blob
                const pdfUrl = URL.createObjectURL(pdfBlob);
                
                // Create preview modal with "Sortir" button
                const previewModalDiv = document.createElement('div');
                document.body.appendChild(previewModalDiv);
                
                const closePreviewModal = () => {
                    URL.revokeObjectURL(pdfUrl);
                    ReactDOM.unmountComponentAtNode(previewModalDiv);
                    document.body.removeChild(previewModalDiv);
                };
                
                ReactDOM.render(
                    <Modal
                        title={`Aperçu Emplacement - ${validDevis.REF_DEV}`}
                        open={true}
                        width="90%"
                        centered={true}
                        closable={true}
                        maskClosable={true}
                        className="pdf-preview-modal"
                        style={{ top: 20, maxWidth: '1400px' }}
                        footer={[
                            <PermissionButton permissionModule="quotes" permissionAction="print" key="print" type="primary" onClick={() => {
                                const printWindow = window.open(pdfUrl);
                                if (printWindow) {
                                    printWindow.addEventListener('load', () => {
                                        printWindow.print();
                                    });
                                } else {
                                    alert("Veuillez autoriser les fenêtres pop-up pour imprimer");
                                }
                            }}>
                                Imprimer
                            </PermissionButton>,
                            <Button key="exit" onClick={closePreviewModal}>
                                Sortir
                            </Button>
                        ]}
                        onCancel={closePreviewModal}
                    >
                        <div className="pdf-preview-container">
                            <iframe
                                src={pdfUrl}
                                style={{ width: '100%', height: '80vh', border: 'none' }}
                                title="Aperçu Emplacement"
                            />
                        </div>
                    </Modal>,
                    previewModalDiv
                );

                return {
                    cleanup: closePreviewModal,
                    pdfUrl
                };
            } catch (previewError) {
                console.error('Error in emplacement preview:', previewError);
                Modal.error({
                    title: 'Erreur d\'aperçu',
                    content: `Une erreur s'est produite lors de l'aperçu: ${previewError.message}`,
                });
            }
        };
        
        // Use ensureDataLoaded with our wrapper
        ensureDataLoaded(devis, items, commercial, performPreview);
        
    } catch (error) {
        console.error('Error previewing devis emplacement:', error);
        Modal.error({
            title: 'Erreur',
            content: `Échec de l'aperçu du devis emplacement: ${error.message}`,
        });
    }
};